import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { Box } from '@mui/material';
import { ColDef } from '@siloreal:modules:private/silobag-list/config/extra/types';
import { FALLBACK_STRING, FieldCellStack, FieldPrimaryText, PLATFORM_GREEN } from './column-field-ui';
import * as HOOKS from '@siloreal:shared:hooks';

type Cfg = { closed?: boolean };

export function buildLifecycleDateColumn(cfg?: Cfg): ColDef {
  const closed = cfg?.closed ?? false;
  const closure = HOOKS.useLocale.getState().t('temp.common.closure');
  const register = HOOKS.useLocale.getState().t('temp.common.register');
  const label = closed ? closure : register;

  return {
    field: 'created_at',
    headerName: label,
    minWidth: 144,
    renderCell: ({ row }) => <LifecycleDateCell value={row.created_at} closed={closed} />,
  };
}

function LifecycleDateCell({ value, closed }: { value: string | null; closed: boolean }) {
  const hasValue = Boolean(value);
  const formatted = formatDate(value);
  const i18n = HOOKS.useLocale();

  return (
    <FieldCellStack>
      <LifecycleIconBadge active={hasValue} closed={closed} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <FieldPrimaryText
          title={hasValue ? formatted : i18n.t('temp.common.date_unavailable')}
          disabled={!hasValue}
        >
          {formatted}
        </FieldPrimaryText>
      </Box>
    </FieldCellStack>
  );
}

function LifecycleIconBadge({ active, closed }: { active: boolean; closed: boolean }) {
  const Icon = closed ? EventBusyOutlinedIcon : EventAvailableOutlinedIcon;

  return (
    <Box
      aria-hidden
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: '6px',
        flexShrink: 0,
        bgcolor: active
          ? closed
            ? 'rgba(73, 69, 79, 0.08)'
            : 'rgba(107, 170, 0, 0.12)'
          : 'rgba(58, 52, 64, 0.06)',
        color: active ? (closed ? '#5c5760' : PLATFORM_GREEN) : '#b8b4ad',
      }}
    >
      <Icon sx={{ fontSize: 15 }} />
    </Box>
  );
}

function formatDate(value: string | null): string {
  if (!value) return FALLBACK_STRING;

  return new Intl.DateTimeFormat('es-AR').format(new Date(value));
}
